import {
  AppstoreOutlined,
  BarChartOutlined,
  CheckOutlined,
  DownOutlined,
  NumberOutlined,
  PlusOutlined,
  SearchOutlined,
  TableOutlined,
  UnorderedListOutlined,
  UpOutlined
} from "@ant-design/icons"
import { Button, Empty, Flex, Skeleton, Tooltip, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { brand, neutral } from "../../../../components/widgets/tokens.ts"
import type {
  CatalogWidgetSpec,
  WidgetCatalog
} from "../../../DashboardPage/redux/widgets.types.ts"
import {
  type LibraryGroupKey,
  LIBRARY_GROUP_ORDER,
  groupCatalog
} from "../../helpers/reportCatalog.ts"

const CATEGORY_KEYS: ("all" | LibraryGroupKey)[] = ["all", ...LIBRARY_GROUP_ORDER]

const GROUP_ICON: Record<LibraryGroupKey, React.ReactNode> = {
  kpi: <NumberOutlined />,
  chart: <BarChartOutlined />,
  table: <TableOutlined />,
  list: <UnorderedListOutlined />,
  other: <AppstoreOutlined />
}

const { Text } = Typography

interface ReportAddWidgetPanelProps {
  catalog: WidgetCatalog | null
  loading?: boolean
  failed?: boolean
  onAdd: (spec: CatalogWidgetSpec) => void
  addedSpecKeys?: ReadonlySet<string>
}

const EMPTY_SPEC_KEYS: ReadonlySet<string> = new Set()

/**
 * The widget catalog: every category and widget the catalog serves, searchable
 * by name. A widget already on the canvas stays listed, marked as added.
 */
export const ReportAddWidgetPanel = ({
  catalog,
  loading = false,
  failed = false,
  onAdd,
  addedSpecKeys = EMPTY_SPEC_KEYS
}: ReportAddWidgetPanelProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState<"all" | LibraryGroupKey>("all")
  const [collapsed, setCollapsed] = useState<Set<LibraryGroupKey>>(new Set())

  const groups = useMemo(() => {
    const all = groupCatalog(catalog, search)
    return category === "all"
      ? all
      : all.filter((group) => group.key === category)
  }, [catalog, search, category])

  const toggleGroup = (key: LibraryGroupKey) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <aside
      aria-label={t("reports.build.addWidget")}
      className="flex w-[300px] flex-none flex-col gap-3 overflow-auto rounded-xl border border-solid bg-white p-4"
      style={{ borderColor: neutral.border }}
    >
      <Text strong className="text-base">
        {t("reports.build.addWidget")}
      </Text>

      <TextInput
        disableAutoCapitalize
        allowClear
        prefix={<SearchOutlined aria-hidden />}
        aria-label={t("reports.library.searchPlaceholder")}
        placeholder={t("reports.library.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div
        role="tablist"
        aria-label={t("reports.build.categoriesLabel")}
        className="flex flex-wrap gap-1.5"
      >
        {CATEGORY_KEYS.map((key) => {
          const active = category === key
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setCategory(key)}
              className="cursor-pointer rounded-md border-0 px-3 py-1 text-[13px]"
              style={{
                background: active ? "rgba(42,125,140,.12)" : "transparent",
                color: active ? brand.teal : neutral.ink,
                fontWeight: active ? 600 : 500
              }}
            >
              {t(`reports.build.categories.${key}`)}
            </button>
          )
        })}
      </div>

      {loading && <Skeleton active paragraph={{ rows: 6 }} title={false} />}

      {!loading && failed && (
        <Text type="danger" className="text-[13px]">
          {t("reports.library.loadFailed")}
        </Text>
      )}

      {!loading && !failed && groups.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("reports.library.empty")}
        />
      )}

      {!loading &&
        groups.map((group) => {
          const open = !collapsed.has(group.key)
          return (
            <section key={group.key} className="flex flex-col gap-1.5">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggleGroup(group.key)}
                className="flex cursor-pointer items-center justify-between border-0 bg-transparent px-0 py-1"
              >
                <Text strong>{t(`reports.library.groups.${group.key}`)}</Text>
                <span aria-hidden style={{ color: neutral.muted }}>
                  {open ? <UpOutlined /> : <DownOutlined />}
                </span>
              </button>
              {open &&
                group.specs.map((spec) => {
                  const added = addedSpecKeys.has(spec.key)
                  return (
                    <Flex
                      key={spec.key}
                      align="center"
                      gap={10}
                      className="rounded-lg border border-solid px-3 py-2"
                      style={{ borderColor: neutral.border }}
                    >
                      <span aria-hidden style={{ color: brand.teal, fontSize: 16 }}>
                        {GROUP_ICON[group.key]}
                      </span>
                      <Text
                        className="min-w-0 flex-1 text-[13px]"
                        type={added ? "secondary" : undefined}
                        ellipsis={{ tooltip: spec.title }}
                      >
                        {spec.title}
                      </Text>
                      <Tooltip
                        title={added ? t("reports.library.alreadyAdded") : ""}
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={added ? <CheckOutlined aria-hidden /> : <PlusOutlined aria-hidden />}
                          aria-label={
                            added
                              ? t("reports.library.addedAria", { name: spec.title })
                              : t("reports.library.addAria", { name: spec.title })
                          }
                          disabled={added}
                          onClick={() => onAdd(spec)}
                          style={{ color: added ? undefined : brand.teal }}
                        />
                      </Tooltip>
                    </Flex>
                  )
                })}
            </section>
          )
        })}
    </aside>
  )
}
